import { Component, OnInit, AfterViewInit, ViewChild, Inject } from '@angular/core';
import { MatTable } from '@angular/material/table';
//import { MatCheckboxChange } from '@angular/material/checkbox';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TooltipPosition } from '@angular/material/tooltip';

import { IHasIdAndName, IHasIdAndNameAndWorkflowId } from '../../interfaces/has-id-and-name.interface';
import { Folder } from '../../models/site-content/folder.model';
import { PickerItem } from '../../models/picker-item.model';
import { MemberTypeEnum } from '../../enums/member-type.enum';
import { MagicStructureMember } from '../../models/magic-structures/magic-structure-member.model';
import { MagicStructureDefinition } from '../../models/magic-structures/magic-structure-def.model';
import { MagicFolderDefinition } from '../../models/magic-structures/magic-folder-def.model';
import { MagicFormInstanceDefinition } from '../../models/magic-structures/magic-form-instance-def.model';
import { MagicStructureTriggerTypeEnum } from '../../enums/magic-structure-trigger-type.enum';
import { MagicStructureMembership, MagicStructureMembershipWithFormGroup } from '../../models/magic-structures/magic-structure-membership.model';
//import { MagicStructureTrigger } from '../../models/magic-structures/magic-structure-trigger.model';
import { MagicStructureService } from '../../services/magic-structure.service';
import { DisplayTimeAndHoursOffset } from '../../../shared/models/misc-metadata/display-time-and-hours-offset.model';
import { MiscellaneousMetadataService } from '../../../shared/services/miscellaneous-metadata.service';
import { DateUtil } from '../../../shared/utility-classes/date.util';
import { WorkflowService } from '../../../shared/services/workflow.service';
import { Workflow } from '../../../shared/models/site-content/workflow.model';
import { DisplayContentDialog, DisplayContentData, DisplayContentDialogInitData } from '../../../shared/dialogs/display-content/display-content.dialog';
import { CsvFileLoader, ParsedCsvLine } from '../../../shared/utility-classes/csv-file.util';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../confirmation/confirmation-dialog.component';
import { IManageMagicStructureMemberships } from '../../../shared/components/magic-struct-membership/magic-struct-membership.component';

// Define titles for trigger types.
const ON_FIRST_ACCESS_TRIGGER: string = 'On First Access';
const NOW_TRIGGER: string = 'Now';
const SCHEDULED_DATE_TRIGGER: string = 'Scheduled Date and Time';

const ADD_NEW_MEMBERSHIP_BUTTON_TITLE: string = 'Add Membership';
const ADD_MEMBER_TO_SELECTED_MEMBERSHIP_BUTTON_TITLE: string = 'Add Member';

const STRUCTURE_NAME_FORM_CONTROL_NAME: string = 'structureName';
const USE_STANDARD_STRUCTURE_NAME_FORM_CONTROL_NAME: string = 'useStandardStructureName';
const STANDARD_STRUCTURE_NAME_FORM_CONTROL_NAME: string = 'standardStructureName';
const ROLE_NAMES_FORM_CONTROL_NAME: string = 'roleNames';
const ONE_STRUCTURE_PER_MEMBERSHIP_MEMBER_CONTROL_NAME: string = 'createOneStructurePerMember';
const TRIGGER_NAME_FORM_CONTROL_NAME: string = 'triggerName';
const SCHEDULED_DATE_FORM_CONTROL_NAME: string = 'scheduledDate';
const SCHEDULED_TIME_FORM_CONTROL_NAME: string = 'scheduledTime';

// Define an internally used interface.
interface ITriggerDisplayNameToEnumValue {
    [displayName: string]: string;
}

// Define an internally used class.
class TriggerDisplayNameToTriggerEnumValue {
    private displayNameToEnumValue: ITriggerDisplayNameToEnumValue = {};

    public constructor() {
        this.displayNameToEnumValue[ON_FIRST_ACCESS_TRIGGER] = MagicStructureTriggerTypeEnum.ON_FIRST_ACCESS;
        this.displayNameToEnumValue[NOW_TRIGGER] = MagicStructureTriggerTypeEnum.NOW;
        this.displayNameToEnumValue[SCHEDULED_DATE_TRIGGER] = MagicStructureTriggerTypeEnum.SCHEDULED_DATE;
    }

    public getEnumValueFor(displayName: string): string {
        return this.displayNameToEnumValue[displayName];
    }
}

@Component({
    selector: 'app-magic-structure-setup',
    templateUrl: './magic-structure-setup.dialog.html',
    styleUrls: ['./magic-structure-setup.dialog.scss'],
    standalone: false
})
export class MagicStructureSetupDialog implements OnInit, AfterViewInit {
    // Properties.
    // Define read-only metadata.
    //private readonly colNames: string[] = ['MembershipName', 'StructureName', 'Member', 'Action'];
    //private readonly colNames: string[] = ['MembershipName', 'RoleNames', 'Member', 'Action'];
    private roleNames: string[] = null;// Note:  this property is treated as read only even though it is not formally a 'readonly' property.
    private readonly triggerNames: string[] = [NOW_TRIGGER, ON_FIRST_ACCESS_TRIGGER, SCHEDULED_DATE_TRIGGER];
    private targetDisplayTimes: DisplayTimeAndHoursOffset[] = null; // Note:  this property is treated as read only even though it is not formally a 'readonly' property.

    // Define properties related to configuring a magic structure.
    private structureBasicInfoFormGroup: UntypedFormGroup = null;
    private membershipGeneralInfoFormGroup: UntypedFormGroup = null;
    private structureTriggerFormGroup: UntypedFormGroup = null;

    private magicStructureDef: MagicStructureDefinition = null; // Allocated in ngOnInit().
    //private structureMemberships: MagicStructureMembershipWithFormGroup[] = [];
    //private localStructureMemberships: MagicStructureMembershipWithFormGroup[] = []; // Will be removed.
    private selectedRoleNames: string[] = null;
    private addMemberButtonCanEnable: boolean = false;

    //private readonly useMembershipComponent: boolean = true;
    private membershipsManager: IManageMagicStructureMemberships = null;

    private triggerDisplayNameToEnumValue: TriggerDisplayNameToTriggerEnumValue = new TriggerDisplayNameToTriggerEnumValue();

    @ViewChild(MatTable) table: MatTable<MagicStructureMember>;
    @ViewChild('importMembershipsFile') importMembershipsFile;

    // Constructor.
    public constructor(private _formBuilder: UntypedFormBuilder,
        public dialogRef: MatDialogRef<MagicStructureSetupDialog>, 
        @Inject(MAT_DIALOG_DATA) public folderOrFormInstance: IHasIdAndNameAndWorkflowId,
        private magicStructureService: MagicStructureService,
        private miscellaneousMetadataService: MiscellaneousMetadataService,
        private workflowService: WorkflowService,
        private dialog: MatDialog) {
        dialogRef.disableClose = true; // Disable closing the dialogue when the user clicks on the page.

        return;
    }

    // Life cycle methods.
    public ngOnInit(): void {
        // Get my structure type.
        this.magicStructureDef =
            (this.folderOrFormInstance.typeName() == Folder.TypeName ?
                new MagicFolderDefinition(this.folderOrFormInstance.getId()) :
                new MagicFormInstanceDefinition(this.folderOrFormInstance.getId()));
        this.magicStructureDef.useStandardStructureName = true;
        this.magicStructureDef.standardStructureName = null;

        // Initialize form data.
        this.getDisplayTimes();

        return;
    }

    public ngAfterViewInit(): void {
        // Note:  when this method is called, the @ViewChild() values should be resolved.
        return;
    }

    // Private getter method.
    private get structureMemberships(): MagicStructureMembershipWithFormGroup[] {
        let memberships: MagicStructureMembershipWithFormGroup[] =
            //(this.useMembershipComponent && this.membershipsManager ? this.membershipsManager.getMemberships() : this.localStructureMemberships);
            (this.membershipsManager != null ? this.membershipsManager.getMemberships() : []);

        return memberships;
    }

    // Accessor methods called by my HTML code.
    public get Title(): string {
        let title: string = `Create Magic ${this.structureTypeName}`;

        return (title);
    }

    public get Initialized(): boolean {
        return (this.structureBasicInfoFormGroup != null) && (this.structureTriggerFormGroup != null);
    }

    public get FolderOrFormInstance(): IHasIdAndNameAndWorkflowId {
        return this.folderOrFormInstance;
    }

    public get SourceStructureTypeName(): string {
        return (this.structureTypeName);
    }
    public get SourceStructureName(): string {
        return (this.folderOrFormInstance.getName());
    }
    public get MagicStructureTypeName(): string {
        let typeName: string = `Magic ${this.structureTypeName}`;

        return (typeName);
    }

    public get UseStandardStructureName(): boolean {
        return (this.magicStructureDef.useStandardStructureName);
    }
    public get StandardStructureName(): string {
        return (this.magicStructureDef.standardStructureName);
    }

    public get HasFolderOrFormInstance(): boolean {
        return (this.folderOrFormInstance != null);
    }

    public get StructureBasicInfoFormGroup(): UntypedFormGroup {
        return (this.structureBasicInfoFormGroup);
    }
    public get MembershipGeneralInfoFormGroup(): UntypedFormGroup {
        return (this.membershipGeneralInfoFormGroup);
    }
    public get StructureTriggerFormGroup(): UntypedFormGroup {
        return (this.structureTriggerFormGroup);
    }

    public get StructureMemberships(): MagicStructureMembership[] {
        return (this.structureMemberships);
    }

    /*
    public get MembershipTableColumnNames(): string[] {
        return (this.colNames);
    }
    */

    public get RoleNames(): string[] {
        return this.roleNames;
    }
    public get RoleNamesDisabled(): boolean {
        return ((this.roleNames == null) || (this.roleNames.length == 0));
    }

    /*
    public get MembershipNameHeaderText(): string {
        //return ('Membership Name');
        let name: string = `${this.structureTypeName} Copy Name`;

        return name;
    }
    */

    /*
    public get StructureNameHeaderText(): string {
        let headerText: string = 'Error';

        if (this.folderOrFormInstance != null) {
            if (this.folderOrFormInstance.typeName() == Folder.TypeName) {
                headerText = 'Folder Name';
            } else {
                headerText = 'Form Name';
            }
        }

        return (headerText);
    }
    */

    public get TooltipPosition(): TooltipPosition {
        let position: TooltipPosition = 'below';

        return (position);
    }
    public get TooltipPositionRight(): TooltipPosition {
        let position: TooltipPosition = 'right';

        return (position);
    }
    public get UseSameStructureNameForAllCopiesTooltip(): string {
        let tooltip: string =
            `Check this and enter a standard ${this.MagicStructureTypeName} name that will be used for all copies.  ` +
            `Uncheck this if you plan to assign a specific name to each copy of a ${this.MagicStructureTypeName} copy.`;

        return (tooltip);
    }
    public get DisableBasicInfoSectionAdvancement(): boolean {
        let bNameMissing: boolean = false;

        if (this.magicStructureDef.useStandardStructureName) {
            if ((this.magicStructureDef.standardStructureName == null) || (this.magicStructureDef.standardStructureName.trim() == '')) {
                bNameMissing = true;
            }
        }

        let bRolesMissing: boolean = ((this.selectedRoleNames == null) || (this.selectedRoleNames.length == 0));

        return (bNameMissing || bRolesMissing);
    }
    public get BasicSectionInfoMissingText(): string {
        let errorMessage: string =
            (this.DisableBasicInfoSectionAdvancement ?
                'A non-blank standard structure name and at least one selected role are required' :
                '');

        return (errorMessage);
    }

    /*
    public get MembershipSelectionCheckboxTooltip(): string {
        let tooltip: string = 'Select a membership checkbox in order to add additional members';

        return (tooltip);
    }
    public MembershipShowHideDetailTooltip(membership: MagicStructureMembership): string {
        let tooltip: string =
            (membership.membershipDetailsExpanded ?
                'Click here to hide membership detail' : 'Click here to show membership detail');

        return (tooltip);
    }
    public get MembershipTrashCanTooltip(): string {
        let tooltip: string = 'Click here to delete this membership';

        return (tooltip);
    }
    public get MoveMemberUpTooltip(): string {
        let tooltip: string = 'Click here to move this member up';

        return (tooltip);
    }
    public get MoveMemberDownTooltip(): string {
        let tooltip: string = 'Click here to move this member down';

        return (tooltip);
    }
    public get DeleteMemberTooltip(): string {
        let tooltip: string = 'Click here to delete this member';

        return (tooltip);
    }
    */

    public get DisableCreateOneStructurePerMemberCheckbox(): boolean {
        return ((this.structureMemberships == null) || (this.structureMemberships.length == 0));
    }
    public get CreateOneStructurePerMemberLabel(): string {
        let label: string = `Create one ${this.structureTypeName.toLowerCase()} per member?`;

        return label;
    }
    public get CreateOneStructurePerMemberTooltip(): string {
        let structureType: string = this.structureTypeName.toLowerCase();
        let tooltip: string = `If checked, creates one ${structureType} copy per member`;

        return tooltip;
    }
    public get NumCopiesConfiguredLabel(): string {
        let label: string = `Number of ${this.structureTypeName.toLowerCase()} copies configured:`;

        return label;
    }
    public get NumCopiesConfiguredValue(): number {
        let numMemberships: number = (this.structureMemberships != null ? this.structureMemberships.length : 0);
        let numCopies: number = numMemberships;

        if (this.magicStructureDef.createOneStructurePerMember && (numCopies > 0)) {
            numCopies = 0;

            for (let iMembership: number = 0; iMembership < this.structureMemberships.length; iMembership++) {
                let membership: MagicStructureMembershipWithFormGroup = this.structureMemberships[iMembership];
                let numMembers: number = (membership.members != null ? membership.members.length : 0);

                numCopies+= numMembers;
            }
        }

        return numCopies;
    }

    /*
    public get MembershipGuidance(): string {
        let guidance: string = 'Select a membership to add members to it; deselect a membership to start a new one.';

        return (guidance);
    }
    */

    /*
    public getName(membership: MagicStructureMembership): string {
        return (membership.name);
    }
    */
    /*
    public getStructureName(membership: MagicStructureMembership): string {
        return (membership.structureName);
    }
    */

    /*
    public MembershipObjectNameDisabled(membership: MagicStructureMembership): boolean {
        let bDisabled: boolean =
            (this.magicStructureDef.useStandardStructureName && (this.magicStructureDef.standardStructureName != null) && (this.magicStructureDef.standardStructureName.trim() != ''));

        return (bDisabled);
    }
    */

    /*
    public getMemberType(membership: MagicStructureMembership): string {
        let memberType: string = null;

        if (membership.members.length == 0) {
            memberType = 'None';
        } else if (membership.members.length == 1) {
            memberType = membership.members[0].memberType;
        } else {
            memberType = membership.members[0].memberType;

            for (let iMember: number = 1; iMember < membership.members.length; iMember++) {
                if (membership.members[iMember].memberType != memberType) {
                    memberType = 'Multiple';

                    break;
                }
            }
        }

        return (memberType);
    }
    */

    /*
    public getMemberDisplayName(membership: MagicStructureMembership): string {
        let member: string = null;

        if (membership.members.length == 0) {
            member = 'None';
        } else if (membership.members.length == 1) {
            let memberType: string = membership.members[0].memberType;
            member = `1 ${memberType}`;
        } else {
            let memberType: string = membership.members[0].memberType;
            member = `${membership.members.length} ${memberType}s`;

            for (let iMember: number = 1; iMember < membership.members.length; iMember++) {
                if (membership.members[iMember].memberType != memberType) {
                    member = `${membership.members.length} members`;

                    break;
                }
            }
        }

        return (member);
    }
    */

    public get DisableMemberSectionAdvancement(): boolean {
        return (this.structureMemberships.length == 0);
    }
    public get AtLeastOneMembershipRequiredText(): string {
        let message: string = (this.structureMemberships.length == 0 ? 'At least one membership must be configured' : '');

        return (message);
    }

    public get ConfigureMembershipsStepName(): string {
        let name: string = `Configure ${this.structureTypeName} Copies`;

        return name;
    }
    public get AddMembershipButtonTitle(): string {
        //return ADD_NEW_MEMBERSHIP_BUTTON_TITLE;
        let title: string = `Add ${this.structureTypeName} Copy`;

        return title;
    }
    public get ImportMembershipsButtonTitle(): string {
        let title: string = `Import ${this.structureTypeName} Copy Data ...`;

        return title;
    }
    public get AddMembershipAdditionalStyle(): string {
        return '';
    }
    public get AddMemberButtonTitle(): string {
        return ADD_MEMBER_TO_SELECTED_MEMBERSHIP_BUTTON_TITLE;
    }
    public get AddMemberLabel(): string {
        let label: string = `Add ${this.structureTypeName} Copy or Member`;

        return label;
    }
    /*
    public get AddMemberAdditionalStyle(): string {
        return '';
    }
    */
    public get AddMemberButtonCanEnable(): boolean {
        return this.addMemberButtonCanEnable;
    }
    public get AddButtonFlyoverText(): string {
        let flyoverText: string = `Click to add a magic ${this.structureTypeName} membership`;

        return flyoverText;
    }
    public get SecondButtonFlyoverText(): string {
        let flyoverText: string = `Click to add another user or group to the selected membership`;

        return flyoverText;
    }

    public get ShowImportFormatButtonDisabled(): boolean {
        let disabled: boolean = false;

        return disabled;
    }

    public get ShowImportMembershipsButtonDisabled(): boolean {
        let disabled: boolean = false;

        return disabled;
    }

    /*
    public showDateAndTimeForTrigger(triggerName: string): boolean {
        return (triggerName == SCHEDULED_DATE_TRIGGER);
    }
    */

    public get TriggerNames(): string[] {
        return (this.triggerNames);
    }
    public get NumTriggerNames(): number {
        return (this.triggerNames.length);
    }
    public IsSelectedTrigger(triggerTypeName: string): boolean {
        let bChecked: boolean = (triggerTypeName == ON_FIRST_ACCESS_TRIGGER);
        bChecked = (triggerTypeName == NOW_TRIGGER); // USED DURING DEVELOPMENT.

        return (bChecked);
    }
    /*
    public get SelectedTriggerType(): string {
        return this.magicStructureDef.triggerType;
    }
    public get ScheduledDateTriggerType(): string {
        return (SCHEDULED_DATE_TRIGGER);
    }
    */
    public get ScheduleFieldsDisabled(): boolean {
        //let disabled: boolean = this.SelectedTriggerType != this.ScheduledDateTriggerType;
        let disabled: boolean = (this.magicStructureDef.triggerType != MagicStructureTriggerTypeEnum.SCHEDULED_DATE);

        return disabled;
    }

    public get MinScheduledDate(): Date {
        let tomorrow: Date = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1); // Add one day.

        return (tomorrow);
    }
    public get MaxScheduledDate(): Date {
        let oneYearFromToday: Date = new Date();
        oneYearFromToday.setDate(oneYearFromToday.getDate() + 365); // Add 365 days.

        return (oneYearFromToday);
    }
    public get ScheduleDateLabelText(): string {
        return ('Schedule date');
    }
    public get ScheduledDatePlaceholderText(): string {
        return ('Select a schedule date');
    }

    public get AvailableScheduleTimes(): DisplayTimeAndHoursOffset[] {
        return this.targetDisplayTimes;
    }
    public get ScheduledTimeLabelText(): string {
        return ('Scheduled time');
    }

    public get ShowDateAndTimeForTrigger(): boolean {
        return (true); // Not yet coded as a conditional.
    }

    private DetailsExpanded(membership: MagicStructureMembership): boolean {
        // Note:  putting this code in a method in case the following,
        //        referenced property, 'membershipDetailsExpanded', is
        //        not actually needed(might be able to use property
        //        'membershipSelected').
        return (membership.membershipDetailsExpanded);
    }

    public MemberDetailRowClass(membership: MagicStructureMembership): string {
        let rowClass: string =
            (this.DetailsExpanded(membership) ? 'expanded-member-detail-row' : 'hidden-member-detail-row');

        return (rowClass);
    }

    public MembershipDetailTdClass(membership: MagicStructureMembership): string {
        let tdClass: string =
            (this.DetailsExpanded(membership) ? 'expanded-detail-td' : 'hidden-detail-td');

        return (tdClass);
    }

    public MemberDetailText(member: MagicStructureMember): string {
        return (member.displayName);
    }
    public MemberTypeText(member: MagicStructureMember): string {
        return (member.memberType);
    }

    public MemberCanMoveUp(structureMembership: MagicStructureMembership, member: MagicStructureMember, memberIndex: number): boolean {
        let bCanMoveUp: boolean =
            ((structureMembership.members.length > 1) && (memberIndex > 0));

        return (bCanMoveUp);
    }
    public MemberCanMoveDown(structureMembership: MagicStructureMembership, member: MagicStructureMember, memberIndex: number): boolean {
        let bCanMoveDown: boolean =
            ((structureMembership.members.length > 1) && (memberIndex < structureMembership.members.length - 1));

        return (bCanMoveDown);
    }

    public get SaveDisabled(): boolean {
        let bDisabled: boolean =
            (this.structureBasicInfoFormGroup == null) ||
            (this.structureBasicInfoFormGroup.controls == null) ||
            (this.structureBasicInfoFormGroup.controls[STRUCTURE_NAME_FORM_CONTROL_NAME] == null) ||
            (this.structureBasicInfoFormGroup.controls[STRUCTURE_NAME_FORM_CONTROL_NAME].value == null) ||
            (this.structureBasicInfoFormGroup.controls[STRUCTURE_NAME_FORM_CONTROL_NAME].value.trim() == '') ||

            (this.structureMemberships == null) ||
            (this.structureMemberships.length == 0) ||

            //(this.selectedTriggerType == null);
            (this.magicStructureDef.triggerType == null);

        return (bDisabled);
    }

    // Handle control events.
    public onMembershipsManagerInit(membershipsManager: IManageMagicStructureMemberships): void {
        this.membershipsManager = membershipsManager;
    }

    public onMembershipChange(magicStructureMemberships: MagicStructureMembershipWithFormGroup[]): void {
        //this.structureMemberships = magicStructureMemberships;
        /*
        if (this.useMembershipComponent && this.membershipsManager) {
            // Nothing to do here as we are being notified of the change.
        } else {
            this.localStructureMemberships = magicStructureMemberships;
        }
        */
    }

    public advanceToMembershipSection(): void {
        // If there are any existing memberships,
        // update them with the latest role names.
        if ((this.structureMemberships != null) && (this.structureMemberships.length > 0)) {
            for (let iMembership: number = 0; iMembership < this.structureMemberships.length; iMembership++) {
                let membership: MagicStructureMembershipWithFormGroup = this.structureMemberships[iMembership];

                membership.updateAvailableRoleNames(this.selectedRoleNames);
            }
        }

        return;
    }

    public addNewMembership(member: PickerItem): void {
        // Add this to the memberships table.
        if (typeof member == 'object') {
            let structureMembership: MagicStructureMembershipWithFormGroup =
                new MagicStructureMembershipWithFormGroup(this._formBuilder, this.selectedRoleNames);

            // Set an initial membership name.
            
            if (member.isUser) {
                structureMembership.name = MagicStructureSetupDialog.getUserMembershipName(member);
            } else {
                structureMembership.name = MagicStructureSetupDialog.getGroupMembershipName(member);
            }

            // Set an initial membership structure name.
            if (this.magicStructureDef.useStandardStructureName &&
                (this.magicStructureDef.standardStructureName != null) &&
                (this.magicStructureDef.standardStructureName.trim() != ''))
            {
                structureMembership.structureName = this.magicStructureDef.standardStructureName;
            } else {
                structureMembership.structureName = structureMembership.name;
            }
            // Setup the memberships structure name form control.
            structureMembership.createFormGroup();

            // Select new memberships by default.
            structureMembership.membershipSelected = true; // Select a new membership by default.
            structureMembership.membershipDetailsExpanded = true; // Show detail for a new membership.

            structureMembership.members = [];
            let firstMember: MagicStructureMember = new MagicStructureMember();
            firstMember.memberType = (member.isUser ? MemberTypeEnum.USER : MemberTypeEnum.GROUP);
            firstMember.member = member.key;
            firstMember.displayName = member.displayName;
            structureMembership.members.push(firstMember);

            let existingMembership: MagicStructureMembership =
                this.structureMemberships.find(m => m.name == structureMembership.name);

            if (existingMembership == undefined) { 
                this.deselectMembershipsExcept(); // Deselect all memberships (in case one is selected).

                /*
                if (this.useMembershipComponent) {
                    if (this.membershipsManager != null) {
                        //this.structureMemberships = this.membershipsManager.addNewMembership(structureMembership, true);
                        this.membershipsManager.addNewMembership(structureMembership, true);
                    }                        
                } else {
                    this.structureMemberships.push(structureMembership);
                    this.table.renderRows();
                }
                */
                if (this.membershipsManager != null)
                    this.membershipsManager.addNewMembership(structureMembership, true);

                this.setAddButtonBasedOnMemberships();

                this.setClearMemberInput(member);
            }
        }

        return;
    }

    public addMemberToMembership(memberPickerItem: PickerItem): void {
        let selectedMembership: MagicStructureMembership =
            this.structureMemberships.find(m => m.membershipSelected == true);

        if ((selectedMembership != null) && (typeof memberPickerItem == 'object')) {
            let structureMember: MagicStructureMember = new MagicStructureMember();
            structureMember.memberType = (memberPickerItem.isUser ? MemberTypeEnum.USER : MemberTypeEnum.GROUP);
            structureMember.member = memberPickerItem.key;
            structureMember.displayName = memberPickerItem.displayName;

            /*
            if (this.useMembershipComponent) {
                if (this.membershipsManager != null) {
                    //this.structureMemberships = this.membershipsManager.addNewMemberToSelectedMembership(structureMember, true);
                    this.membershipsManager.addNewMemberToSelectedMembership(structureMember, true);
                }                    
            } else {
                this.addMemberToSelectedMembership(selectedMembership, structureMember);
            }
            */
            if (this.membershipsManager != null)
                this.membershipsManager.addNewMemberToSelectedMembership(structureMember, true);

            this.setClearMemberInput(structureMember);
        }
    }

    //public addMemberToSelectedMembership(structureMembership: MagicStructureMembership,member: PickerItem): void {
    public addMemberToSelectedMembership(structureMembership: MagicStructureMembership, structureMember: MagicStructureMember): void {
        // Add this to the members table.
        /*
        if (typeof member == 'object') {
            let structureMember: MagicStructureMember = new MagicStructureMember();
            structureMember.memberType = (member.isUser ? MemberTypeEnum.USER : MemberTypeEnum.GROUP);
            structureMember.member = member.key;
            structureMember.displayName = member.displayName;

            // Has this member already been added?
            let existingMember: MagicStructureMember =
                structureMembership.members.find(sm => (sm.memberType == structureMember.memberType) && (sm.member == structureMember.member));

            if (existingMember == undefined) {
                structureMembership.members.push(structureMember);

                this.table.renderRows();

                this.setClearMemberInput(member);
            }
        }
        */
        // Has this member already been added?
        let existingMember: MagicStructureMember =
            structureMembership.members.find(sm => (sm.memberType == structureMember.memberType) && (sm.member == structureMember.member));

        if (existingMember == undefined) {
            structureMembership.members.push(structureMember);
            this.table.renderRows();

            //this.setClearMemberInput(structureMember);
        }

        return;
    }

    //public membershipSelectionChanged(structureMembership: MagicStructureMembershipWithFormGroup): void {
    public onMembershipSelectionChanged(structureMembership: MagicStructureMembershipWithFormGroup): void {
        if (structureMembership.membershipSelected) {
            // Deselect any other selected membership.
            this.deselectMembershipsExcept(structureMembership);
        } 

        this.setAddButtonBasedOnMemberships();

        return;
    }

    public arrowsExpandClicked(membership: MagicStructureMembership, eventData: any): void {
        // Toggle whether the specified membership's details are displayed.
        membership.membershipDetailsExpanded = (!membership.membershipDetailsExpanded);

        return;
    }

    public deleteMembership(structureMembership: MagicStructureMembership): void {
        /*
        let numMemberships: number = this.structureMemberships.length;

        let filteredMemberships: MagicStructureMembershipWithFormGroup[] =
            this.structureMemberships.filter(m => m != structureMembership);
        */

        /*
        if (filteredMemberships.length != numMemberships) {
            if (!this.useMembershipComponent) {
                //this.structureMemberships = filteredMemberships;
                this.localStructureMemberships = filteredMemberships;

                if (structureMembership.membershipSelected) {
                    //this.setAddButtonToAddMember(false);
                    this.setAddButtonBasedOnMemberships();
                }

                this.table.renderRows();
            }
        }
        */
        this.setAddButtonBasedOnMemberships();

        return;
    }

    public moveMemberUp(structureMembership: MagicStructureMembership, member: MagicStructureMember, memberIndex: number): void {
        if (memberIndex > 0) {
            let member: MagicStructureMember = structureMembership.members[memberIndex];

            // Remove the member and re-add it.
            structureMembership.members =
                structureMembership.members.filter(sm => (sm.memberType != member.memberType) || (sm.member != member.member));

            structureMembership.members.splice(memberIndex - 1, 0, member);

            this.table.renderRows();
        }

        return;
    }
    public moveMemberDown(structureMembership: MagicStructureMembership, member: MagicStructureMember, memberIndex: number): void {
        if (memberIndex < structureMembership.members.length - 1) {
            let member: MagicStructureMember = structureMembership.members[memberIndex];

            // Remove the member and re-add it.
            structureMembership.members =
                structureMembership.members.filter(sm => (sm.memberType != member.memberType) || (sm.member != member.member));

            structureMembership.members.splice(memberIndex + 1, 0, member);

            this.table.renderRows();
        }

        return;
    }
    public deleteMember(structureMembership: MagicStructureMembership,member: MagicStructureMember,memberIndex: number): void {
        let numMembers: number = structureMembership.members.length;

        let filteredMembers: MagicStructureMember[] =
            structureMembership.members.filter(sm => (sm.memberType != member.memberType) || (sm.member != member.member));

        if (filteredMembers.length != numMembers) {
            structureMembership.members = filteredMembers;

            this.table.renderRows();
        }

        return;
    }

    /*
    public triggerValueChanged(eventData: MatCheckboxChange, triggerName: string): void {
        if (eventData.checked) {
            let existingSelectedTrigger: string = this.selectedTriggerTypes.find(t => t == triggerName);

            if (existingSelectedTrigger == undefined) {
                this.selectedTriggerTypes.push(triggerName);
            }
        } else {
            this.selectedTriggerTypes = this.selectedTriggerTypes.filter(t => t != triggerName);
        }

        return;
    }
    */

    public save(): void {
        // 08-05-2021 TO DO:
        //     1. Add check for unique membership structure names;
        //     2. Add error message on the last form indicating that
        //        at least one membership is required to save;
        //     3. Convert the dialogue to inherit from the async
        //        job dialogue base and make it stay open while it
        //        creates the magic structure;
        //     4. Test creating magic form instances.

        // Assemble and return a
        // magic structure definition.
        let magicStructureDef: MagicStructureDefinition = this.packageMagicStructureDef();

        if (this.folderOrFormInstance.typeName() == Folder.TypeName) {
            let magicFolderDef = <MagicFolderDefinition>magicStructureDef;

            this.magicStructureService.createMagicFolder(magicFolderDef).then(createdMagicFolder => {
                this.dialogRef.close(magicStructureDef);
            });
        } else {
            let magicFormInstanceDef = <MagicFormInstanceDefinition>magicStructureDef;

            this.magicStructureService.createMagicFormInstance(magicFormInstanceDef).then(createdMagicFormInstance => {
                this.dialogRef.close(magicStructureDef);
            });
        }        

        return;
    }

    public cancel(): void {
        this.dialogRef.close(null);

        return;
    }

    public showImportFormat(): void {
        let magicStructureDef: MagicStructureDefinition = this.packageMagicStructureDef();
        if ((magicStructureDef.memberships == null) || (magicStructureDef.memberships.length == 0)) {
            magicStructureDef = <MagicStructureDefinition>magicStructureDef.clone();

            let exampleMembership: MagicStructureMembership = MagicStructureMembership.createExampleMembership(this.structureTypeName, this.roleNames);
            magicStructureDef.memberships.push(exampleMembership);
        }

        this.magicStructureService.getMembershipImportFormatExample(magicStructureDef).then(textResponse => {
            this.displayImportFormatContent(textResponse);
        });
    }

    public importMemberships(): void {
        // Process an open file dialogue.
        this.importMembershipsFile.nativeElement.click();
    }

    public onImportMembershipsFileChanged(eventData: any): void {
        if ((this.importMembershipsFile.nativeElement.files !== null) &&
            (this.importMembershipsFile.nativeElement.files.length > 0)) {
            if ((this.structureMemberships != null) && (this.structureMemberships.length > 0)) {
                // Confirm the import.
                let confirmationMessage: string = 'Load membership data from a CSV file, replacing existing memberships?';
                let confirmationData: ConfirmationDialogModel = new ConfirmationDialogModel('Replace memberships', confirmationMessage);

                let dialogRef = this.dialog.open(ConfirmationDialogComponent, { width: "400px", data: confirmationData });

                dialogRef.afterClosed().subscribe(yes => {
                    if (yes) {
                        // Process an open file dialogue.
                        this.loadMembershipsCsvFile(eventData);
                    }
                });
            } else {
                // Process an open file dialogue.
                this.loadMembershipsCsvFile(eventData);
            }
        }
    }

    private loadMembershipsCsvFile(eventData: any): void {
        this.loadMembershipsFromCsvData(eventData);
        this.importMembershipsFile.nativeElement.value = null;
    }

    private loadMembershipsFromCsvData(eventData: any): void {
        if ((this.importMembershipsFile.nativeElement.files !== null) &&
            (this.importMembershipsFile.nativeElement.files.length > 0)) {
            // Get the selected file.
            let file: File = this.importMembershipsFile.nativeElement.files[0];
            let filename: string = file.name;

            let csvFileLoader: CsvFileLoader = new CsvFileLoader(file);
            let promise: Promise<ParsedCsvLine[]> = csvFileLoader.loadCsvFile();
            promise.then(parsedCsvLines => {
                /*
                if (this.useMembershipComponent && this.membershipsManager) {
                    this.membershipsManager.clearMemberships();
                } else {
                    //this.structureMemberships = [];
                    this.localStructureMemberships = [];
                }
                */
                if (this.membershipsManager != null)
                    this.membershipsManager.clearMemberships();

                this.loadCsvMembershipData(parsedCsvLines);
            });
        }

        return;
    }

    private loadCsvMembershipData(parsedLinesOfText: ParsedCsvLine[]): void {
        if ((parsedLinesOfText != null) && (parsedLinesOfText.length > 1)) { // Skip the first/header line.
            // First pass:  compile a list of referenced role names.
            let referencedRoleNames: string[] = [];

            for (let iLine: number = 1; iLine < parsedLinesOfText.length; iLine++) {
                let parsedLine: ParsedCsvLine = parsedLinesOfText[iLine];
                let parsedData: string[] = parsedLine.ParsedData;

                if ((parsedData != null) && (parsedData.length == 4)) {
                    let roleNamesText: string = parsedData[1];
                    let roleNames: string[] = roleNamesText.split('|');

                    for (let iRole: number = 0; iRole < roleNames.length; iRole++) {
                        let roleName: string = roleNames[iRole];

                        if ((referencedRoleNames.find(r => r == roleName) == null) &&
                            (this.roleNames.find(r => r == roleName) != null)) {
                            referencedRoleNames.push(roleName);
                        }                            
                    }
                }
            }

            //this.selectedRoleNames = referencedRoleNames;
            let roleNamesFC: UntypedFormControl = <UntypedFormControl>this.structureBasicInfoFormGroup.get(ROLE_NAMES_FORM_CONTROL_NAME);
            //roleNamesFC.value = this.selectedRoleNames;
            roleNamesFC.setValue(referencedRoleNames);
            let testValue = roleNamesFC.value;

            // Second pass:  load memberships.
            {
                let lastMembershipAdded: MagicStructureMembershipWithFormGroup = null;

                for (let iLine: number = 1; iLine < parsedLinesOfText.length; iLine++) {
                    let parsedLine: ParsedCsvLine = parsedLinesOfText[iLine];
                    let parsedData: string[] = parsedLine.ParsedData;

                    if ((parsedData != null) && (parsedData.length == 4)) {
                        // Process this membership member.
                        let membershipName: string = parsedData[0];
                        let roleNamesText: string = parsedData[1];
                        let roleNames: string[] = roleNamesText.split('|');
                        let memberType: string = parsedData[2];
                        let member: string = parsedData[3];

                        let existingMembership: MagicStructureMembershipWithFormGroup = this.structureMemberships.find(m => m.name == membershipName);
                        let membership: MagicStructureMembershipWithFormGroup =
                            (existingMembership != null ? existingMembership : new MagicStructureMembershipWithFormGroup(this._formBuilder, this.selectedRoleNames));

                        if (existingMembership == null) {
                            membership.name = membershipName;
                            membership.structureName = this.magicStructureDef.standardStructureName;
                            membership.setRoleNames(roleNames);
                            membership.members = [];

                            membership.createFormGroup();

                            /*
                            if (this.useMembershipComponent) {
                                if (this.membershipsManager != null) {
                                    if (lastMembershipAdded != null)
                                        lastMembershipAdded.membershipSelected = false;

                                    membership.membershipSelected = true;
                                    this.membershipsManager.addNewMembership(membership, false);
                                }
                            } else {
                                this.structureMemberships.push(membership);
                            }
                            */
                            if (this.membershipsManager != null) {
                                if (lastMembershipAdded != null)
                                    lastMembershipAdded.membershipSelected = false;

                                membership.membershipSelected = true;
                                this.membershipsManager.addNewMembership(membership, false);
                            }

                            lastMembershipAdded = membership;
                        }

                        let structureMember: MagicStructureMember = new MagicStructureMember();
                        structureMember.memberType = ((memberType != null) && (memberType.toLowerCase() == 'user') ? MemberTypeEnum.USER : MemberTypeEnum.GROUP);
                        structureMember.member = member;
                        structureMember.displayName = member;

                        /*
                        if (this.useMembershipComponent) {
                            if (this.membershipsManager != null) 
                                this.membershipsManager.addNewMemberToSelectedMembership(structureMember, false);
                        } else {
                            membership.members.push(structureMember);
                        }
                        */
                        if (this.membershipsManager != null)
                            this.membershipsManager.addNewMemberToSelectedMembership(structureMember, false);
                    } // if
                } // for

                // Finish up.
                if (lastMembershipAdded != null)
                    lastMembershipAdded.membershipSelected = false;
                //if ((this.useMembershipComponent) && (this.membershipsManager != null))
                if (this.membershipsManager != null)
                    this.membershipsManager.renderRows();
            } // block
        } // if

        return;
    }

    // Private helper methods.
    private setupFormControls(): void {
        // Set up initial control values.
        this.selectedRoleNames = (this.roleNames != null ? [this.roleNames[0]] : []);
        let dateValue: string = DateUtil.TodayToUTCDateString();
        let timeValue: number = this.targetDisplayTimes[0].hoursOffset;
        let selectedTriggerType: MagicStructureTriggerTypeEnum = MagicStructureTriggerTypeEnum.NOW;

        // Setup the template name and related form fields.
        this.magicStructureDef.standardStructureName = this.folderOrFormInstance.getName();

        this.structureBasicInfoFormGroup = this._formBuilder.group({
            structureName: [`${this.folderOrFormInstance.getName()} ${this.MagicStructureTypeName}`, Validators.required],
            useStandardStructureName: [this.magicStructureDef.useStandardStructureName],
            standardStructureName: [{ value: this.magicStructureDef.standardStructureName, disabled: false }, Validators.required],
            roleNames: [this.selectedRoleNames]
        });

        // Note:  form groups for memberships are stored/managed in
        //        instances of class MagicStructureMembershipWithFormGroup.
        // Setup the 'magic' structure trigger form fields.
        this.membershipGeneralInfoFormGroup = this._formBuilder.group({
            createOneStructurePerMember: [this.magicStructureDef.createOneStructurePerMember]
        });

        this.structureTriggerFormGroup = this._formBuilder.group({
            triggerName: [selectedTriggerType, Validators.required],

            scheduledDate: [dateValue],
            scheduledTime: [timeValue]
        });

        // Listen for template name and related value changes.
        let structureNameFC: UntypedFormControl = <UntypedFormControl>this.structureBasicInfoFormGroup.get(STRUCTURE_NAME_FORM_CONTROL_NAME);
        structureNameFC.valueChanges.subscribe(value => {
            //console.log('Structure name changed ...');
        });
        let useStandardStructureNameFC: UntypedFormControl = <UntypedFormControl>this.structureBasicInfoFormGroup.get(USE_STANDARD_STRUCTURE_NAME_FORM_CONTROL_NAME);
        useStandardStructureNameFC.valueChanges.subscribe(bValue => {
            let standardStructureNameFC: UntypedFormControl = <UntypedFormControl>this.structureBasicInfoFormGroup.get(STANDARD_STRUCTURE_NAME_FORM_CONTROL_NAME);

            //this.bUseStandardStructureName = bValue;
            this.magicStructureDef.useStandardStructureName = bValue;
            if (this.magicStructureDef.useStandardStructureName) {
                standardStructureNameFC.enable();
            } else {
                let standardStructureNameFC: UntypedFormControl = <UntypedFormControl>this.structureBasicInfoFormGroup.get(STANDARD_STRUCTURE_NAME_FORM_CONTROL_NAME);
                standardStructureNameFC.setValue('');

                standardStructureNameFC.disable();
            }
        });
        let standardStructureNameFC: UntypedFormControl = <UntypedFormControl>this.structureBasicInfoFormGroup.get(STANDARD_STRUCTURE_NAME_FORM_CONTROL_NAME);
        standardStructureNameFC.valueChanges.subscribe(value => {
            this.magicStructureDef.standardStructureName = value;
        });

        let roleNamesFC: UntypedFormControl = <UntypedFormControl>this.structureBasicInfoFormGroup.get(ROLE_NAMES_FORM_CONTROL_NAME);
        roleNamesFC.valueChanges.subscribe(value => {
            this.selectedRoleNames = value;
        });

        // Listen for general membership info (not info for a given membership).
        let oneStructurePerMemberFC: UntypedFormControl = <UntypedFormControl>this.membershipGeneralInfoFormGroup.get(ONE_STRUCTURE_PER_MEMBERSHIP_MEMBER_CONTROL_NAME);
        oneStructurePerMemberFC.valueChanges.subscribe(value => {
            this.magicStructureDef.createOneStructurePerMember = value;
        });

        // Listen for trigger value changes.
        let triggerNameFC: UntypedFormControl = <UntypedFormControl>this.structureTriggerFormGroup.get(TRIGGER_NAME_FORM_CONTROL_NAME);
        triggerNameFC.valueChanges.subscribe(val => {
            //this.magicStructureDef.triggerType = val;
            this.magicStructureDef.triggerType = this.triggerDisplayNameToEnumValue.getEnumValueFor(val);

            let scheduledDateFC: UntypedFormControl = <UntypedFormControl>this.structureTriggerFormGroup.get(SCHEDULED_DATE_FORM_CONTROL_NAME);
            let scheduledTimeFC: UntypedFormControl = <UntypedFormControl>this.structureTriggerFormGroup.get(SCHEDULED_TIME_FORM_CONTROL_NAME);
            // Note:  the following approach to setting or removing the required validator on the scheduled date
            //        and time form control only works in simple case, including these cases as each form control
            //        has at most one validator.
            //
            // Note:  using conditional validators is discussed in the following article:
            //            https://medium.com/ngx/3-ways-to-implement-conditional-validation-of-reactive-forms-c59ed6fc3325
            if (this.magicStructureDef.triggerType == SCHEDULED_DATE_TRIGGER) {
                scheduledDateFC.setValidators(Validators.required);
                scheduledTimeFC.setValidators(Validators.required);
            } else {
                scheduledDateFC.clearValidators();
                scheduledTimeFC.clearValidators();
            }
        });
        let scheduledTime: UntypedFormControl = <UntypedFormControl>this.structureTriggerFormGroup.get(SCHEDULED_TIME_FORM_CONTROL_NAME);
        scheduledTime.valueChanges
            .subscribe(timeValue => {
                console.log('Time changed.');
            });

        let useStandardStructureFC: UntypedFormControl = <UntypedFormControl>this.structureBasicInfoFormGroup.get(USE_STANDARD_STRUCTURE_NAME_FORM_CONTROL_NAME);
        useStandardStructureFC.valueChanges
            .subscribe(bValue => {
                let standardStructureNameFC: UntypedFormControl = <UntypedFormControl>this.structureBasicInfoFormGroup.get(STANDARD_STRUCTURE_NAME_FORM_CONTROL_NAME);

                if (bValue == true) {
                    standardStructureNameFC.enable();
                } else {
                    standardStructureNameFC.disable();
                }
            });

        return;
    }

    private packageMagicStructureDef(): MagicStructureDefinition {
        this.magicStructureDef.name = this.structureBasicInfoFormGroup.controls[STRUCTURE_NAME_FORM_CONTROL_NAME].value;
        this.magicStructureDef.copiesFolderName = `${this.magicStructureDef.name} Copies`;

        this.magicStructureDef.memberships = [];
        for (let iMembership: number = 0; iMembership < this.structureMemberships.length; iMembership++) {
            let membershipWithFormGroup: MagicStructureMembershipWithFormGroup = this.structureMemberships[iMembership];
            let membershipWithoutFormGroup: MagicStructureMembership = membershipWithFormGroup.asMagicStructureMembership();

            this.magicStructureDef.memberships.push(membershipWithoutFormGroup);
        }

        if (this.magicStructureDef.triggerType == MagicStructureTriggerTypeEnum.SCHEDULED_DATE) {
            let scheduledDateFC: UntypedFormControl = <UntypedFormControl>this.structureTriggerFormGroup.get(SCHEDULED_DATE_FORM_CONTROL_NAME);
            let scheduledTime: UntypedFormControl = <UntypedFormControl>this.structureTriggerFormGroup.get(SCHEDULED_TIME_FORM_CONTROL_NAME);
            this.magicStructureDef.scheduledTriggerDateTime = DateUtil.DateToUTCDate(scheduledDateFC.value, 0, scheduledTime.value);
            this.magicStructureDef.scheduledTriggerHoursOffset = scheduledTime.value;
        }

        return (this.magicStructureDef);
    }

    private deselectMembershipsExcept(membershipException: MagicStructureMembership = null): void {
        for (let iMembership: number = 0; iMembership < this.structureMemberships.length; iMembership++) {
            let membership: MagicStructureMembership = this.structureMemberships[iMembership];

            if ((membershipException == null) || (membershipException != membership)) {
                if (membership.membershipSelected) {
                    membership.membershipSelected = false;
                }
            }
        }
    }

    private setAddButtonBasedOnMemberships(): void {
        let bMembershipSelected: boolean = false;

        for (let iMembership: number = 0; iMembership < this.structureMemberships.length; iMembership++) {
            let membership: MagicStructureMembership = this.structureMemberships[iMembership];

            if (membership.membershipSelected) {
                bMembershipSelected = true;

                break;
            }
        }

        this.setMembershipSelected(bMembershipSelected);

        return;
    }

    private setMembershipSelected(bSelectedFlag: boolean): void {
        this.addMemberButtonCanEnable = bSelectedFlag;
    }

    private setClearMemberInput(memberOutput: any): void {
        memberOutput['clearMemberInput'] = true;

        return;
    }

    private static getUserMembershipName(member: PickerItem): string {
        let name: string = '';

        if (member.secondaryDisplayName != null) {
            name = member.secondaryDisplayName;

            // See if we can get the first part of the email address as the proposed membership name.
            let userNameParts: string[] = member.secondaryDisplayName.split('@');
            if ((userNameParts != null) && (userNameParts.length > 1)) {
                name = userNameParts[0];

                // Can we simplify the name more?
                userNameParts = name.split('_');
                if ((userNameParts != null) && (userNameParts.length > 1)) {
                    let simplerName: string = `${userNameParts[0].charAt(0)}${userNameParts[userNameParts.length - 1]}`;
                    name = simplerName;
                }
            }
        } else if (member.displayName != null) {
            name = member.displayName;
        } else {
            name = member.key;
        }

        return name;
    }

    private static getGroupMembershipName(member: PickerItem): string {
        let name: string = '';

        if (member.displayName != null) {
            name = member.displayName;
        } else if (member.key != null) {
            name = member.key;
        } else if (member.fullDisplay != null) {
            name = member.fullDisplay;
        }

        return name;
    }

    private getDisplayTimes(): void {
        this.miscellaneousMetadataService.getDisplayTimes().then(response => {
            this.targetDisplayTimes = response;

            this.getWorkflowDef();
        });
    }

    private getWorkflowDef(): void {
        let workflowId: number = this.folderOrFormInstance.getWorkflowId();
        this.workflowService.get(workflowId).then(workflow => {
            this.roleNames = workflow.getRoleNames();

            this.setupFormControls();
        })
    }

    private get structureTypeName(): string {
        return (this.folderOrFormInstance.typeName() == Folder.TypeName ? 'Folder' : 'Form');
    }

    private displayImportFormatContent(textContent: string): void {
        let contentData: DisplayContentData[] =
            [
                { content: textContent, tabName: 'CSV Format', makeContentReadonly: true }
            ];
        let initData: DisplayContentDialogInitData = new DisplayContentDialogInitData('Import Format Example', contentData);

        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';
        dialogConfig.height = '615px';
        dialogConfig.data = initData;

        const dialogRef = this.dialog.open(DisplayContentDialog, dialogConfig);
        dialogRef.afterClosed().subscribe(na => {
        });
    }
}
